import React from 'react'
import { graphql } from 'gatsby'
import clx from 'classnames'
import { useLocation } from '@reach/router'
import queryString from 'query-string'
import Layout from '../components/Layout'
import SEO from '../components/Seo'
import Article, { ArticleData } from '../components/Article'
import { TagData } from '../components/Tag'
import { Link as Tag } from '../components/Button'
import styles from './Home.module.css'
import { SearchResults } from '../components/Search'
import Pagination, { PageInfo } from '../components/Pagination'
import Banner from '../components/Banner'

interface HomeProps {
  data: IndexData
}

const getSearchQuery = query => {
  if (query) {
    const queriedSearch = queryString.parse(query)
    const { s = '' } = queriedSearch
    return s
  }
  return ''
}

const Home: React.FC<HomeProps> = ({ data, pageContext }) => {
  const { basePath } = pageContext
  const { nodes: articles, pageInfo } = data.allWpPost
  const { currentPage } = pageInfo
  const topArticles = articles.slice(0, 3)
  const bottomArticles = articles.slice(3, articles.length)
  const location = useLocation()
  const searchQuery = (location.search && getSearchQuery(location.search)) || ''
  const isPaged = currentPage > 1
  const title = `${data.site.siteMetadata.title}${
    isPaged ? ` - Page ${currentPage}` : ''
  }`
  const canonical = currentPage <= 1 ? '/' : `/page/${currentPage}/`

  if (searchQuery) {
    return (
      <Layout>
        <SEO title={`Search ${searchQuery}`} />
        <h1>Search: "{searchQuery}"</h1>
        <SearchResults
          searchIndex={data.siteSearchIndex.index}
          query={searchQuery}
        />
      </Layout>
    )
  }

  return (
    <Layout isHome page={currentPage}>
      <SEO
        title={title}
        description={data.site.siteMetadata.description}
        canonical={canonical}
      />
      {/* <HomeLead /> */}
      <h4 className={clx(styles.sectionTitle, isPaged && styles.paged)}>
        Latest Articles{isPaged && ` - Page ${currentPage}`}
      </h4>
      <section className={clx(styles.row, isPaged && styles.paged)}>
        <div className={styles.col}>
          <Article key={topArticles[0].slug} data={topArticles[0]} card />
        </div>
        {topArticles.length >= 2 && (
          <div className={clx(styles.aside, styles.noMb)}>
            <Article
              key={topArticles[1].slug}
              data={topArticles[1]}
              card
              aside
            />
            {topArticles.length > 2 && (
              <Article
                key={topArticles[2].slug}
                data={topArticles[2]}
                card
                aside
              />
            )}
          </div>
        )}
      </section>
      <section className={clx(styles.row, isPaged && styles.paged)}>
        <section className={styles.col}>
          <Banner
            className="adsbygoogle"
            style={{ display: 'block' }}
            slot="3996326775"
            format="auto"
            responsive="true"
          />
          {bottomArticles.map(node => (
            <Article key={node.slug} data={node} />
          ))}

          <Pagination
            pageInfo={pageInfo}
            basePath={basePath}
            baseTitle={data.site.siteMetadata.title}
            className={styles.pagination}
          />
        </section>
        {!isPaged && (
          <aside className={clx(styles.aside, styles.sticky)}>
            <section className={styles.asideSection}>
              <h4 className={styles.sectionTitle}>Categories</h4>
              {data.categories.nodes.map(({ link, name, slug, uri }) => (
                <Tag key={slug} to={link} title={name} className={styles.tag}>
                  {name}
                </Tag>
              ))}
            </section>
            <section className={styles.asideSection}>
              <h4 className={styles.sectionTitle}>Programming Languages</h4>
              {data.programmingLanguages.nodes.map(
                ({ link, name, slug, uri }) => (
                  <Tag key={slug} to={link} title={name} className={styles.tag}>
                    {name}
                  </Tag>
                )
              )}
            </section>
            <section className={styles.asideSection}>
              <h4 className={styles.sectionTitle}>Popular Topics</h4>
              {data.tags.nodes.map(({ link, name, slug, uri }) => (
                <Tag key={slug} to={link} title={name} className={styles.tag}>
                  {name}
                </Tag>
              ))}
            </section>
          </aside>
        )}
      </section>
    </Layout>
  )
}

export default Home

interface IndexData {
  allWpPost: {
    pageInfo: PageInfo
    nodes: ArticleData[]
  }
  categories: {
    nodes: TagData[]
  }
  programmingLanguages: {
    nodes: TagData[]
  }
  tags: {
    nodes: TagData[]
  }
  site: { siteMetadata: { description: string; title: string } }
}

export const pageQuery = graphql`
  query indexQuery($skip: Int!, $limit: Int!) {
    siteSearchIndex {
      index
    }
    site {
      siteMetadata {
        description
        title
      }
    }
    allWpPost(
      sort: { fields: [isSticky, date], order: [DESC, DESC] }
      limit: $limit
      skip: $skip
    ) {
      pageInfo {
        perPage
        currentPage
        hasNextPage
        hasPreviousPage
        pageCount
      }
      nodes {
        title
        excerpt
        slug
        uri
        date(formatString: "DD MMM, yyyy")
        dateGmt
        categories {
          nodes {
            uri
            name
          }
        }
        featuredImage {
          node {
            altText
            seo {
              metaDesc
            }
            mobileImage: localFile {
              childImageSharp {
                fluid(
                  maxWidth: 120
                  maxHeight: 120
                  cropFocus: CENTER
                  quality: 100
                ) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
            desktopImage: localFile {
              childImageSharp {
                fluid(
                  maxHeight: 200
                  maxWidth: 200
                  cropFocus: CENTER
                  quality: 100
                ) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
            fullImage: localFile {
              childImageSharp {
                fluid(
                  maxHeight: 400
                  maxWidth: 650
                  cropFocus: CENTER
                  quality: 100
                ) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
          }
        }
      }
    }
    categories: allWpCategory(
      filter: { count: { gt: 0 } }
      sort: { fields: count, order: DESC }
    ) {
      nodes {
        description
        name
        link
        nodeType
        slug
        uri
      }
    }
    programmingLanguages: allWpTag(
      filter: { language: { language: { eq: true } }, count: { gt: 0 } }
      sort: { fields: count, order: DESC }
    ) {
      nodes {
        description
        name
        link
        nodeType
        slug
        uri
      }
    }
    tags: allWpTag(
      filter: { language: { language: { ne: true } }, count: { gt: 1 } }
      sort: { fields: count, order: DESC }
    ) {
      nodes {
        description
        name
        link
        nodeType
        slug
        uri
      }
    }
  }
`
